import { FC } from 'react';
import { InvestorProfile } from '@/types/index';

interface Props {
  profile: Partial<InvestorProfile>;
  onChange: (data: Partial<InvestorProfile>) => void;
}

const OBJECTIFS = [
  { value: 'CASHFLOW', label: 'Cash flow', desc: 'Générer des revenus mensuels immédiats', icon: '💸' },
  { value: 'CAPITAL', label: 'Plus-value', desc: 'Valorisation du patrimoine sur le long terme', icon: '📈' },
  { value: 'MIXTE', label: 'Mixte', desc: 'Équilibre rendement et plus-value', icon: '⚖️' },
  { value: 'SECURITE', label: 'Sécurité', desc: 'Préserver le capital, risque minimal', icon: '🛡️' },
];

const HORIZONS = [
  { value: 'COURT', label: 'Court terme', desc: '< 5 ans' },
  { value: 'MOYEN', label: 'Moyen terme', desc: '5 – 10 ans' },
  { value: 'LONG', label: 'Long terme', desc: '> 10 ans' },
];

export const Step1Objectifs: FC<Props> = ({ profile, onChange }) => {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Objectif principal</h3>
        <div className="grid grid-cols-2 gap-3">
          {OBJECTIFS.map(o => (
            <button
              key={o.value}
              onClick={() => onChange({ objectifPrincipal: o.value as InvestorProfile['objectifPrincipal'] })}
              className={`p-4 rounded-xl border-2 text-left transition-all
                ${profile.objectifPrincipal === o.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'}`}
            >
              <div className="text-2xl mb-1">{o.icon}</div>
              <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{o.label}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{o.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Horizon d'investissement</h3>
        <div className="flex gap-3">
          {HORIZONS.map(h => (
            <button
              key={h.value}
              onClick={() => onChange({ horizon: h.value as InvestorProfile['horizon'] })}
              className={`flex-1 p-3 rounded-xl border-2 text-center transition-all
                ${profile.horizon === h.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'}`}
            >
              <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{h.label}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{h.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            ROE cible <span className="text-gray-400">(%/an)</span>
          </label>
          <input
            type="number"
            step="0.5"
            min="0"
            max="30"
            value={profile.roeCible ?? ''}
            onChange={e => onChange({ roeCible: e.target.value ? parseFloat(e.target.value) : null })}
            placeholder="ex: 8"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Cash flow cible <span className="text-gray-400">(CHF/mois)</span>
          </label>
          <input
            type="number"
            step="100"
            min="0"
            value={profile.cashflowCible ?? ''}
            onChange={e => onChange({ cashflowCible: e.target.value ? parseFloat(e.target.value) : null })}
            placeholder="ex: 500"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nb biens cible
          </label>
          <input
            type="number"
            step="1"
            min="1"
            max="100"
            value={profile.nbBiensCible ?? ''}
            onChange={e => onChange({ nbBiensCible: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="ex: 3"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );
};
